import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import api from "../../utils/api";
import { getImageUrl } from "../../utils/imageUtils";
import { usePopup } from "../../context/PopupContext";
import { FaUpload, FaXmark } from "react-icons/fa6";
import "./FarmerAddEditProduct.css";

const FarmerAddEditProduct = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = usePopup();
  const isEditMode = !!id;
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    unit: "kg",
    category: "vegetables",
    stock: "",
    minOrder: "1",
    maxOrder: "10",
    isOrganic: false,
    image: "",
    images: [],
    imageFile: null, // Store the actual file for upload
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (
          userObj.role !== "farmer" &&
          !(userObj.role === "admin" && isEditMode)
        ) {
          navigate("/");
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    } else {
      navigate("/login");
    }

    if (isEditMode) {
      fetchProduct();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode, navigate]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/products/${id}`);
      const product = response.data;

      setFormData({
        name: product.name || "",
        description: product.description || "",
        price: product.price?.toString() || "",
        unit: product.unit || "kg",
        category: (product.category || "vegetables").toLowerCase(),
        stock: product.quantity?.toString() || "",
        minOrder: "1",
        maxOrder: "10",
        isOrganic: product.isOrganic || false,
        image:
          product.images && product.images.length > 0
            ? getImageUrl(product.images[0])
            : "",
        images: product.images || [],
      });
    } catch (error) {
      console.error("Error fetching product:", error);
      showToast(t("farmerAddEditProduct.failedToLoad"), "error");
      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        navigate(u.role === "admin" ? "/admin/products" : "/farmer/products");
      } catch {
        navigate("/farmer/products");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file.type.startsWith("image/")) {
      // Store the file for upload
      setFormData({ ...formData, imageFile: file });

      // Create preview using FileReader
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          image: reader.result,
          imageFile: file,
        }));
      };
      reader.readAsDataURL(file);
    } else {
      showToast(t("farmerAddEditProduct.uploadImageFile"), "info");
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim())
      newErrors.name = t("farmerAddEditProduct.productNameRequired");
    if (!formData.description.trim())
      newErrors.description = t("farmerAddEditProduct.descriptionRequired");
    if (!formData.price || parseFloat(formData.price) <= 0)
      newErrors.price = t("farmerAddEditProduct.validPriceRequired");
    if (!formData.stock || parseInt(formData.stock) < 0)
      newErrors.stock = t("farmerAddEditProduct.validStockRequired");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSaving(true);

    try {
      // Create FormData to support file upload
      const productFormData = new FormData();
      productFormData.append("name", formData.name);
      productFormData.append("description", formData.description);
      productFormData.append("category", formData.category.toLowerCase());
      productFormData.append("price", parseFloat(formData.price));
      productFormData.append("quantity", parseInt(formData.stock));
      productFormData.append("unit", formData.unit);
      productFormData.append("isOrganic", formData.isOrganic);
      productFormData.append("isAvailable", true);

      // Add image file if it exists
      if (formData.imageFile) {
        productFormData.append("image", formData.imageFile);
      }

      if (isEditMode) {
        await api.put(`/products/${id}`, productFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        showToast(t("farmerAddEditProduct.productUpdated"), "success");
      } else {
        await api.post("/products", productFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        showToast(t("farmerAddEditProduct.productAdded"), "success");
      }

      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        navigate(u.role === "admin" ? "/admin/products" : "/farmer/products");
      } catch {
        navigate("/farmer/products");
      }
    } catch (error) {
      console.error("Error saving product:", error);
      const errorMessage =
        error.response?.data?.message || t("farmerAddEditProduct.failedToSave");
      showToast(errorMessage, "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="farmer-add-edit-product-page">
        <Header />
        <div className="farmer-add-edit-product-loading-state">
          <div className="farmer-add-edit-product-spinner"></div>
          <p>{t("farmerAddEditProduct.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="farmer-add-edit-product-page">
      <Header />

      <div className="farmer-add-edit-product-container-modern">
        <div className="farmer-add-edit-product-page-header">
          <h1 className="farmer-add-edit-product-page-title">
            {isEditMode
              ? t("farmerAddEditProduct.editProduct")
              : t("farmerAddEditProduct.addNewProduct")}
          </h1>
          {isEditMode && (
            <p className="farmer-add-edit-product-last-updated">
              {t("farmerAddEditProduct.lastUpdated", {
                date: new Date().toLocaleDateString(),
              })}
            </p>
          )}
        </div>

        <div className="farmer-add-edit-product-form-container">
          <form
            onSubmit={handleSubmit}
            className="farmer-add-edit-product-form"
          >
            {/* Product Name */}
            <div className="farmer-add-edit-product-form-group">
              <label htmlFor="name">
                {t("farmerAddEditProduct.productName")}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`farmer-add-edit-product-form-input ${errors.name ? "error" : ""}`}
                required
              />
              {errors.name && (
                <span className="farmer-add-edit-product-error-message">
                  {errors.name}
                </span>
              )}
            </div>

            {/* Category */}
            <div className="farmer-add-edit-product-form-group">
              <label htmlFor="category">
                {t("farmerAddEditProduct.category")}
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="farmer-add-edit-product-form-input"
                required
              >
                <option value="vegetables">Vegetables</option>
                <option value="fruits">Fruits</option>
                <option value="grains">Grains</option>
                <option value="dairy">Dairy</option>
                <option value="spices">Spices</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Price */}
            <div className="farmer-add-edit-product-form-group">
              <label htmlFor="price">{t("farmerAddEditProduct.price")}</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                min="0"
                step="0.01"
                className={`farmer-add-edit-product-form-input ${errors.price ? "error" : ""}`}
                required
              />
              {errors.price && (
                <span className="farmer-add-edit-product-error-message">
                  {errors.price}
                </span>
              )}
            </div>

            {/* Stock Quantity */}
            <div className="farmer-add-edit-product-form-group">
              <label htmlFor="stock">
                {t("farmerAddEditProduct.stockQuantity", {
                  unit: formData.unit,
                })}
              </label>
              <input
                type="number"
                id="stock"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                min="0"
                className={`farmer-add-edit-product-form-input ${errors.stock ? "error" : ""}`}
                required
              />
              {errors.stock && (
                <span className="farmer-add-edit-product-error-message">
                  {errors.stock}
                </span>
              )}
            </div>

            {/* Unit */}
            <div className="farmer-add-edit-product-form-group">
              <label htmlFor="unit">{t("farmerAddEditProduct.unit")}</label>
              <select
                id="unit"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                className="farmer-add-edit-product-form-input"
                required
              >
                <option value="kg">kg</option>
                <option value="gram">gram</option>
                <option value="piece">piece</option>
                <option value="liter">liter</option>
                <option value="packet">packet</option>
              </select>
            </div>

            {/* Organic */}
            <div className="farmer-add-edit-product-form-group">
              <label
                className="farmer-add-edit-product-checkbox-label"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  name="isOrganic"
                  checked={!!formData.isOrganic}
                  onChange={handleChange}
                />
                <span>{t("farmerAddEditProduct.organicProduct")}</span>
              </label>
              <p
                className="farmer-add-edit-product-field-hint"
                style={{ marginTop: "4px", fontSize: "13px", color: "#666" }}
              >
                {t("farmerAddEditProduct.organicHint")}
              </p>
            </div>

            {/* Description */}
            <div className="farmer-add-edit-product-form-group">
              <label htmlFor="description">
                {t("farmerAddEditProduct.description")}
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                className={`farmer-add-edit-product-form-input ${errors.description ? "error" : ""}`}
                required
                placeholder={t("farmerAddEditProduct.descriptionPlaceholder")}
              ></textarea>
              {errors.description && (
                <span className="farmer-add-edit-product-error-message">
                  {errors.description}
                </span>
              )}
            </div>

            {/* Image Upload */}
            <div className="farmer-add-edit-product-form-group">
              <label>{t("farmerAddEditProduct.productImage")}</label>
              <div
                ref={dropZoneRef}
                className={`farmer-add-edit-product-image-upload-zone ${dragActive ? "active" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                {formData.image &&
                (formData.image.startsWith("/") ||
                  formData.image.startsWith("http") ||
                  formData.image.startsWith("data:")) ? (
                  <div className="farmer-add-edit-product-uploaded-image">
                    <img src={formData.image} alt="Uploaded" />
                    <button
                      type="button"
                      className="farmer-add-edit-product-remove-image-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData({
                          ...formData,
                          image: "",
                          imageFile: null,
                        });
                      }}
                    >
                      <FaXmark />
                    </button>
                  </div>
                ) : (
                  <div className="farmer-add-edit-product-upload-placeholder">
                    <FaUpload
                      style={{ fontSize: "32px", marginBottom: "8px" }}
                    />
                    <p>{t("farmerAddEditProduct.uploadImage")}</p>
                    <p className="farmer-add-edit-product-upload-hint">
                      {t("farmerAddEditProduct.dragAndDrop")}
                    </p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileInput}
              />
            </div>

            {/* Form Actions */}
            <div className="farmer-add-edit-product-form-actions">
              <Link
                to="/farmer/products"
                className="farmer-add-edit-product-btn-cancel"
              >
                Cancel
              </Link>
              <button
                type="submit"
                className="farmer-add-edit-product-btn-save"
                disabled={saving}
              >
                {saving
                  ? t("farmerAddEditProduct.saving")
                  : isEditMode
                    ? t("farmerAddEditProduct.updateProduct")
                    : t("farmerAddEditProduct.saveProduct")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FarmerAddEditProduct;
